package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState

private val VerdeAbogado = Color(0xFF1D9E75)

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun PerfilAbogadoScreen(
    abogadoId: String,
    navController: NavController? = null,
    viewModel: MarketplaceViewModel,
) {
    val perfilState by viewModel.perfilAbogadoState.collectAsState()
    val guardarState by viewModel.guardarPerfilState.collectAsState()

    var isEditing by remember { mutableStateOf(false) }

    var nombreCompleto by remember { mutableStateOf("") }
    var matricula by remember { mutableStateOf("") }
    var jurisdiccion by remember { mutableStateOf("Rosario - Santa Fe") }
    var especialidadesTexto by remember { mutableStateOf("Laboral, Civil y Comercial") }
    var biografia by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        viewModel.cargarPerfilAbogado(if (abogadoId == "current") null else abogadoId)
    }

    LaunchedEffect(perfilState) {
        if (perfilState is UiState.Success) {
            val model = (perfilState as UiState.Success<PerfilAbogadoDataModel?>).data
            if (model != null) {
                nombreCompleto = model.nombreCompleto
                matricula = model.matricula.orEmpty()
                jurisdiccion = model.jurisdiccion.ifBlank { "Rosario - Santa Fe" }
                especialidadesTexto = model.especialidades.joinToString(", ")
                biografia = model.biografia
            }
        }
    }

    LaunchedEffect(guardarState) {
        if (guardarState is UiState.Success) {
            viewModel.resetGuardarPerfilState()
            isEditing = false
        }
    }

    Column(modifier = Modifier.fillMaxSize()) {
        MktHeader(
            titulo = "Perfil Profesional",
            subtitulo = "Marketplace Legal · Rosario",
            onBack = { navController?.popBackStack() },
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            if (isEditing) {
                // ── Formulario de Edición ─────────────────────────
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(20.dp),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    elevation = CardDefaults.cardElevation(2.dp),
                ) {
                    Column(
                        modifier = Modifier.padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(14.dp),
                    ) {
                        Text(
                            text = "Editar Perfil Profesional",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = VerdeAbogado,
                        )

                        OutlinedTextField(
                            value = nombreCompleto,
                            onValueChange = { nombreCompleto = it },
                            label = { Text("Nombre Completo") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                        )

                        OutlinedTextField(
                            value = matricula,
                            onValueChange = { matricula = it },
                            label = { Text("Matrícula Profesional") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                        )

                        OutlinedTextField(
                            value = jurisdiccion,
                            onValueChange = { jurisdiccion = it },
                            label = { Text("Jurisdicción / Zona") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                        )

                        OutlinedTextField(
                            value = especialidadesTexto,
                            onValueChange = { especialidadesTexto = it },
                            label = { Text("Especialidades (separadas por coma)") },
                            modifier = Modifier.fillMaxWidth(),
                        )

                        OutlinedTextField(
                            value = biografia,
                            onValueChange = { biografia = it },
                            label = { Text("Biografía / Presentación") },
                            modifier = Modifier.fillMaxWidth(),
                            minLines = 3,
                        )

                        if (guardarState is UiState.Error) {
                            Text(
                                text = (guardarState as UiState.Error).message,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodySmall,
                            )
                        }

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            OutlinedButton(
                                onClick = { isEditing = false },
                                modifier = Modifier.weight(1f),
                            ) {
                                Text("Cancelar")
                            }

                            Button(
                                onClick = {
                                    val listaEspecialidades = especialidadesTexto
                                        .split(",")
                                        .map { it.trim() }
                                        .filter { it.isNotBlank() }

                                    viewModel.guardarPerfilAbogado(
                                        nombreCompleto = nombreCompleto,
                                        matricula = matricula,
                                        jurisdiccion = jurisdiccion,
                                        especialidades = listaEspecialidades,
                                        biografia = biografia,
                                    )
                                },
                                modifier = Modifier.weight(1f),
                                colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado),
                                enabled = guardarState !is UiState.Loading && nombreCompleto.isNotBlank(),
                            ) {
                                if (guardarState is UiState.Loading) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(20.dp),
                                        color = Color.White,
                                    )
                                } else {
                                    Text("Guardar Perfil")
                                }
                            }
                        }
                    }
                }
            } else {
                // ── Vista Pública / Detalle del Perfil ────────────
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(24.dp),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    elevation = CardDefaults.cardElevation(3.dp),
                ) {
                    Column(
                        modifier = Modifier.padding(24.dp).fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        Box(
                            modifier = Modifier
                                .size(80.dp)
                                .background(VerdeAbogado.copy(alpha = 0.15f), CircleShape)
                                .border(3.dp, VerdeAbogado, CircleShape),
                            contentAlignment = Alignment.Center,
                        ) {
                            val iniciales = nombreCompleto
                                .ifBlank { "Dr" }
                                .split(" ")
                                .take(2)
                                .joinToString("") { it.take(1).uppercase() }
                            Text(
                                text = iniciales,
                                style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.ExtraBold),
                                color = VerdeAbogado,
                            )
                        }

                        Text(
                            text = if (nombreCompleto.isNotBlank()) "Dr./Dra. $nombreCompleto" else "Perfil del Profesional",
                            style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.ExtraBold),
                            color = MaterialTheme.colorScheme.onSurface,
                        )

                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Surface(shape = RoundedCornerShape(8.dp), color = VerdeAbogado.copy(alpha = 0.1f)) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                                ) {
                                    Icon(Icons.Default.Badge, null, tint = VerdeAbogado, modifier = Modifier.size(13.dp))
                                    Text(
                                        text = if (matricula.isNotBlank()) "Mat. $matricula" else "Matrícula Pendiente",
                                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                        color = VerdeAbogado,
                                    )
                                }
                            }
                            Surface(shape = RoundedCornerShape(8.dp), color = MaterialTheme.colorScheme.surfaceVariant) {
                                Row(
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                                ) {
                                    Icon(Icons.Default.LocationOn, null, tint = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.size(13.dp))
                                    Text(
                                        text = jurisdiccion,
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    )
                                }
                            }
                        }
                    }
                }

                // ── Biografía / Presentación ────────────────────────
                if (biografia.isNotBlank()) {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(20.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                        elevation = CardDefaults.cardElevation(2.dp),
                    ) {
                        Column(
                            modifier = Modifier.padding(20.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            Text(
                                text = "Presentación Profesional",
                                style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.Bold),
                                color = VerdeAbogado,
                            )
                            Text(
                                text = biografia,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurface,
                            )
                        }
                    }
                }

                // ── Especialidades ─────────────────────────────────
                val especialidadesList = especialidadesTexto
                    .split(",")
                    .map { it.trim() }
                    .filter { it.isNotBlank() }

                if (especialidadesList.isNotEmpty()) {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(20.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                        elevation = CardDefaults.cardElevation(2.dp),
                    ) {
                        Column(
                            modifier = Modifier.padding(20.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                            ) {
                                Icon(Icons.Default.Gavel, null, tint = VerdeAbogado, modifier = Modifier.size(18.dp))
                                Text(
                                    text = "Especialidades",
                                    style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.Bold),
                                    color = MaterialTheme.colorScheme.onSurface,
                                )
                            }
                            FlowRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                            ) {
                                especialidadesList.forEach { esp ->
                                    Surface(shape = RoundedCornerShape(20.dp), color = VerdeAbogado.copy(alpha = 0.1f)) {
                                        Text(
                                            text = esp,
                                            modifier = Modifier.padding(horizontal = 14.dp, vertical = 6.dp),
                                            style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.SemiBold),
                                            color = VerdeAbogado,
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                // ── Botón Editar Perfil ─────────────────────────────
                OutlinedButton(
                    onClick = { isEditing = true },
                    modifier = Modifier.fillMaxWidth().height(50.dp),
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = VerdeAbogado),
                    border = androidx.compose.foundation.BorderStroke(1.5.dp, VerdeAbogado),
                ) {
                    Icon(Icons.Default.Edit, null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Editar Perfil", fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}
